import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import SensorStatusDot from 'commons/src/components/sensors/SensorStatusDot';
import { sensorGraphColors, sensorUnits, colors } from 'commons/src/constants';
import { EnumStatusColors } from 'commons/src/models/commonEnums';

type Props = {
    thresholds: number[];
    devicesInEachLevel: { good: number; fair: number; poor: number };
    unit: keyof typeof sensorUnits | undefined;
};

const ThresholdLevelsView = ({ thresholds, unit, devicesInEachLevel }: Props): React.ReactElement => {
    const { t: txt } = useTranslation();
    const thresholdColors = (colorIndex: number, sensorType: string): EnumStatusColors => {
        if (colorIndex < 0) return EnumStatusColors.red;
        const indexedSensor: keyof typeof sensorGraphColors =
            `${colorIndex}${sensorType}` as keyof typeof sensorGraphColors;
        const colorCode = sensorGraphColors[indexedSensor];
        if (colorCode === colors.graphGreen) return EnumStatusColors.green;
        if (colorCode === colors.graphYellow) return EnumStatusColors.yellow;
        return EnumStatusColors.red;
    };
    const headerValues = ['ThresholdKeys.NormalLevel', 'ThresholdKeys.ActionLevel', 'ThresholdKeys.WarningLevel'];

    const thresholdValues = (): React.ReactElement | React.ReactElement[] =>
        thresholds.length > 0 ? (
            [thresholds[0], ...thresholds].map((threshold, index) => {
                const color = thresholdColors(index, 'radon');
                let devicesOnLevel = devicesInEachLevel.good;
                if (color === EnumStatusColors.red) devicesOnLevel = devicesInEachLevel.poor;
                else if (color === EnumStatusColors.yellow) devicesOnLevel = devicesInEachLevel.fair;
                const elementKey = `threshold-value-${color}-${devicesOnLevel}-${index}`;
                return (
                    <div
                        key={elementKey}
                        className={classNames('insight-tile__content__text', 'insight-tile__content__thresholds__row', {
                            'insight-tile__content__thresholds__row--no-border': index === thresholds.length,
                        })}
                    >
                        <div>
                            <SensorStatusDot color={color} />
                            <span className="insight-tile__content__text--large">{txt(headerValues[index])} </span>
                            <div className="insight-tile__content__thresholds__margin-row">
                                {index === 0 ? '<' : '≥'}
                                {threshold} {unit && sensorUnits[unit]}
                                {index !== 0 &&
                                    index !== thresholds.length && [
                                        '  ',
                                        '< ',
                                        thresholds[index],
                                        ' ',
                                        unit && sensorUnits[unit],
                                    ]}
                            </div>
                        </div>
                        <div className="text-large">
                            {devicesOnLevel} {devicesOnLevel === 1 ? txt('DeviceLowerCase') : txt('DevicesLowerCase')}
                        </div>
                    </div>
                );
            })
        ) : (
            <div />
        );
    return (
        <div className="insight-tile__content__thresholds">
            <h4>{txt('RadonInsight.DevicesWithinEachThreshold')}</h4>
            {thresholdValues()}
        </div>
    );
};

export default ThresholdLevelsView;
